/*!
 * Equal Eats – variant search (type-to-search language picker)
 * Progressive enhancement: turns long option dropdowns (card language) into an
 * accessible combobox. The original control stays in the DOM (visually hidden)
 * and remains the source of truth; choosing a value sets it and fires the same
 * 'change' event the theme/apps already listen to. No JS = original dropdown.
 *
 * Handles:
 *   - Impulse variant dropdowns          select[data-variant-input]
 *   - Card Finder quiz dropdowns         select.cf__select[data-variant]
 *   - Globo Product Options (language)   .gpo-element select[name] (language-named only)
 *   - Upsell Koala bundle tiers          koala-dropdown
 * Opt-in for anything else: add data-variant-search to a <select>.
 * Opt-out: add data-variant-search="off" to a <select> / koala-dropdown or an ancestor.
 */
(function () {
  'use strict';
  if (window.__variantSearchLoaded) return;
  window.__variantSearchLoaded = true;

  var CFG = Object.assign({
    minOptions: 9,                // enhance when > 8 real options...
    nameRe: /(language|langue|idioma|sprache|lingua|taal|jezik|język|língua|sprog|språk|kieli|dil\b|язык|語|언어)/i, // ...or the option is a language
    placeholderRe: /^\s*[\(\*\-–—\s]*\s*(select|please|pease|choose|pick|--)/i,
    gpoMinOptions: 30,            // Globo selects: language-named, or long lists such as the ~670 allergens
    headerRe: /^\s*(quick selection\b.*|-{2,}.*-{2,})\s*$/i, // pseudo-headings inside Globo allergen lists
    renderLimit: 50,              // max options painted at once (lists can hold 670+)
    debounceMs: 90                // input debounce for long lists
  }, window.VariantSearchConfig || {});

  var I18N = Object.assign({
    placeholder: 'Type to search…',
    placeholderLanguage: 'Type to search languages…',
    noResults: 'No matches for “[q]”',
    more: 'Showing [shown] of [total] – keep typing to narrow the list',
    results: '[n] results available',
    oneResult: '1 result available',
    showAll: 'Show all options',
    soldOut: (window.theme && theme.strings && theme.strings.soldOut) || 'Sold out',
    unavailable: (window.theme && theme.strings && theme.strings.unavailable) || 'Unavailable'
  }, window.VariantSearchStrings || {});

  /* Extra search terms (endonyms / common synonyms) so e.g. "español", "mandarin", "farsi" find the English label. */
  var ALIASES = {
    spanish: 'español espanol castellano castilian', french: 'français francais', german: 'deutsch',
    italian: 'italiano', portuguese: 'português portugues', dutch: 'nederlands flemish vlaams', japanese: '日本語 nihongo',
    korean: '한국어 hangul', chinese: '中文 mandarin cantonese zhongwen putonghua', 'simplified chinese': '简体',
    greek: 'ελληνικά ellinika', russian: 'русский russkiy', ukrainian: 'українська', arabic: 'العربية', hebrew: 'עברית ivrit',
    hindi: 'हिन्दी', thai: 'ไทย', vietnamese: 'tiếng việt tieng viet', turkish: 'türkçe turkce', polish: 'polski',
    czech: 'čeština cestina', slovak: 'slovenčina slovencina', slovenian: 'slovenščina slovenscina slovene', croatian: 'hrvatski',
    serbian: 'srpski српски', bosnian: 'bosanski', montenegrin: 'crnogorski', macedonian: 'македонски', bulgarian: 'български',
    romanian: 'română romana', hungarian: 'magyar', swedish: 'svenska', norwegian: 'norsk bokmål', danish: 'dansk',
    finnish: 'suomi', icelandic: 'íslenska', estonian: 'eesti', latvian: 'latviešu', lithuanian: 'lietuvių', albanian: 'shqip',
    catalan: 'català', maltese: 'malti', indonesian: 'bahasa indonesia', malay: 'bahasa melayu', tagalog: 'filipino pilipino',
    khmer: 'cambodian ខ្មែរ', lao: 'laotian ລາວ', bengali: 'bangla বাংলা', nepali: 'नेपाली', punjabi: 'ਪੰਜਾਬੀ', sinhala: 'sinhalese සිංහල',
    swahili: 'kiswahili', persian: 'farsi فارسی', 'latam': 'latin america latinoamérica mexico', brazil: 'brasil brazilian', english: 'uk us british american'
  };

  var uid = 0;
  function norm(s) {
    return String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
      .replace(/[\u{1F1E6}-\u{1F1FF}]/gu, '').replace(/\s+/g, ' ').trim();
  }
  function esc(s) { return String(s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function tpl(s, k, v) { return s.replace('[' + k + ']', v); }
  function optedOut(el) { var o = el.closest('[data-variant-search]'); return o && o.getAttribute('data-variant-search') === 'off'; }
  function aliasFor(label) {
    var n = norm(label), extra = [];
    Object.keys(ALIASES).forEach(function (k) { if (n.indexOf(k) !== -1) extra.push(ALIASES[k]); });
    return norm(extra.join(' '));
  }
  function cleanLabel(s) { return String(s || '').replace(/\s+-\s+[^-]*\d[^-]*$/, '').replace(/\s+/g, ' ').trim(); }

  /* ---------------- Adapters: one per kind of original control ---------------- */

  function SelectAdapter(sel, kind) {
    this.el = sel; this.kind = kind;
  }
  SelectAdapter.prototype = {
    label: function () {
      if (this._label) return this._label;
      var s = this.el, l = s.id && document.querySelector('label[for="' + CSS.escape(s.id) + '"]');
      if (!l) { var g = s.closest('.gpo-element'); l = g && g.querySelector('.gpo-label .label-content, .gpo-label'); }
      this._label = { el: l || null, text: l ? l.textContent.replace(/\s+/g, ' ').trim() : (s.getAttribute('name') || s.getAttribute('aria-label') || '') };
      return this._label;
    },
    options: function () {
      if (this._opts && !this._dirty) return this._opts;
      var out = [], placeholder = '';
      Array.prototype.forEach.call(this.el.options, function (o, i) {
        var text = o.textContent.replace(/\s+/g, ' ').trim();
        if (o.value === '' || (i === 0 && CFG.placeholderRe.test(text))) { if (!placeholder) placeholder = text; return; }
        if (CFG.headerRe.test(text)) { out.push({ value: o.value, label: text.replace(/^-+|-+$/g, '').trim(), header: true, disabled: true }); return; }
        var dis = o.disabled || o.classList.contains('disabled');
        var sold = /sold out|unavailable/i.test(text) || o.getAttribute('data-sold-out') === 'true';
        out.push({ value: o.value, label: text.replace(/\s+[–-]\s+sold out$/i, ''), disabled: dis, soldOut: dis || sold });
      });
      this._opts = { list: out, placeholder: placeholder }; this._dirty = false;
      return this._opts;
    },
    value: function () { var o = this.el.options[this.el.selectedIndex]; return o && o.value !== '' ? o.value : null; },
    set: function (v) {
      if (this.el.value === v) return;
      this.el.value = v;
      this.el.dispatchEvent(new Event('input', { bubbles: true }));
      this.el.dispatchEvent(new Event('change', { bubbles: true }));
    },
    hide: function () { this.el.classList.add('vs-original'); this.el.setAttribute('tabindex', '-1'); this.el.setAttribute('aria-hidden', 'true'); },
    observe: function (cb) {
      var self = this;
      this.el.addEventListener('change', cb);
      this._mo = new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) if (muts[i].type === 'childList' || muts[i].target !== self.el) { self._dirty = true; break; }
        cb();
      });
      this._mo.observe(this.el, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'class', 'selected'] });
      return function () { self.el.removeEventListener('change', cb); self._mo.disconnect(); };
    },
    hiddenByOwner: function () { return this.el.closest('[hidden]') !== null; }
  };

  function KoalaAdapter(el) { this.el = el; this.kind = 'koala'; }
  KoalaAdapter.prototype = {
    label: function () { return { el: null, text: this.el.getAttribute('name') || '' }; },
    options: function () {
      var raw = []; try { raw = JSON.parse(this.el.getAttribute('options') || '[]'); } catch (e) {}
      var out = [], placeholder = this.el.getAttribute('placeholder') || '';
      raw.forEach(function (o, i) {
        var label = String(o.label != null ? o.label : o.value).replace(/<[^>]*>/g, '');
        if (CFG.placeholderRe.test(label) && i === 0) { placeholder = label; return; }
        out.push({ value: String(o.value), label: label, disabled: !!o.disabled, soldOut: !!o.disabled });
      });
      return { list: out, placeholder: placeholder };
    },
    value: function () {
      var v = this.el.getAttribute('selected'), opts = this.options().list;
      for (var i = 0; i < opts.length; i++) if (opts[i].value === v) return v;
      return null;
    },
    set: function (v) {
      if (this.el.getAttribute('selected') === v) return;
      // Prefer the app's own click path (user-initiated selection); fall back to its observed 'selected' attribute.
      var item = this.el.querySelector('.koala-dropdown-item[data-value="' + CSS.escape(v) + '"]');
      if (item) item.click(); else this.el.setAttribute('selected', v);
    },
    hide: function () { this.el.classList.add('vs-original'); this.el.setAttribute('aria-hidden', 'true'); var t = this.el.querySelector('[tabindex]'); if (t) t.setAttribute('tabindex', '-1'); },
    observe: function (cb) {
      var self = this;
      this._mo = new MutationObserver(function () { var t = self.el.querySelector('.koala-dropdown[tabindex="0"]'); if (t) t.setAttribute('tabindex', '-1'); cb(); });
      this._mo.observe(this.el, { attributes: true, attributeFilter: ['selected', 'options', 'disabled'], childList: true });
      this.el.addEventListener('change', cb);
      return function () { self._mo.disconnect(); self.el.removeEventListener('change', cb); };
    },
    disabled: function () { return this.el.hasAttribute('disabled'); },
    hiddenByOwner: function () { return false; }
  };

  /* ---------------- Combobox widget ---------------- */

  function Combobox(adapter) {
    this.a = adapter;
    this.id = 'vs-' + (++uid);
    this.open = false; this.active = -1; this.matches = []; this.data = adapter.options();
    this.isLanguage = CFG.nameRe.test(adapter.label().text);
    this.big = this.data.list.length > 200;
    this.build();
  }
  Combobox.prototype.build = function () {
    var a = this.a, lab = a.label(), self = this;
    var root = document.createElement('div');
    root.className = 'vs-combobox vs-combobox--' + a.kind;
    root.setAttribute('data-variant-search-widget', '');
    var labelHtml = '';
    if (!lab.el && lab.text) labelHtml = '<label class="vs-label" for="' + this.id + '-input">' + esc(lab.text) + '</label>';
    root.innerHTML = labelHtml +
      '<div class="vs-field">' +
        '<input id="' + this.id + '-input" class="vs-input" type="text" role="combobox" autocomplete="off" autocapitalize="off" autocorrect="off" spellcheck="false" enterkeyhint="done"' +
        ' aria-autocomplete="list" aria-expanded="false" aria-controls="' + this.id + '-list" aria-haspopup="listbox">' +
        '<button type="button" class="vs-toggle" tabindex="-1" aria-label="' + esc(I18N.showAll) + '" aria-controls="' + this.id + '-list" aria-expanded="false">' +
          '<svg aria-hidden="true" focusable="false" viewBox="0 0 20 20" width="12" height="12"><path d="M5 7.5l5 5 5-5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
        '</button>' +
      '</div>' +
      '<ul class="vs-list" id="' + this.id + '-list" role="listbox" hidden></ul>' +
      '<div class="vs-status" role="status" aria-live="polite"></div>';
    this.root = root;
    this.input = root.querySelector('.vs-input');
    this.list = root.querySelector('.vs-list');
    this.toggleBtn = root.querySelector('.vs-toggle');
    this.status = root.querySelector('.vs-status');
    if (lab.el) {
      // Re-point the existing theme/app label at our input so the visible label still names the control.
      if (!lab.el.id) lab.el.id = this.id + '-label';
      if (lab.el.tagName === 'LABEL') lab.el.setAttribute('for', this.id + '-input');
      else if (lab.el.closest('label')) lab.el.closest('label').setAttribute('for', this.id + '-input');
      this.input.setAttribute('aria-labelledby', lab.el.id);
      this.list.setAttribute('aria-labelledby', lab.el.id);
    } else if (lab.text) {
      this.list.setAttribute('aria-label', lab.text);
    }

    a.el.insertAdjacentElement('afterend', root);
    a.hide();

    // Keep our own input/change/keydown events from reaching theme/app delegated listeners.
    ['input', 'change'].forEach(function (t) { root.addEventListener(t, function (e) { if (e.target === self.input) { e.stopPropagation(); if (t === 'input') self.onInput(); } }); });
    root.addEventListener('click', function (e) { e.stopPropagation(); }, false);

    this.input.addEventListener('focus', function () { self.input.select(); self.show(''); });
    this.input.addEventListener('click', function () { if (!self.open) { self.input.select(); self.show(''); } });
    this.input.addEventListener('keydown', function (e) { self.onKey(e); });
    this.input.addEventListener('blur', function () { setTimeout(function () { if (!root.contains(document.activeElement)) self.close(true); }, 120); });
    this.toggleBtn.addEventListener('mousedown', function (e) { e.preventDefault(); });
    this.toggleBtn.addEventListener('click', function (e) {
      e.preventDefault();
      if (self.open) { self.close(true); } else { self.input.focus(); self.show(''); }
    });
    // pointerdown + preventDefault keeps focus in the input so blur doesn't close before the pick registers.
    this.list.addEventListener('mousedown', function (e) { e.preventDefault(); });
    this.list.addEventListener('click', function (e) {
      var li = e.target.closest('[role="option"]');
      if (!li || li.getAttribute('aria-disabled') === 'true') return;
      self.choose(li.getAttribute('data-value'));
      // On touch devices drop focus so the keyboard closes after picking.
      if (window.matchMedia && matchMedia('(pointer: coarse)').matches) self.input.blur();
    });
    document.addEventListener('pointerdown', function (e) { if (self.open && !root.contains(e.target)) self.close(true); }, true);

    this.unobserve = a.observe(function () { self.refresh(); });
    this.refresh();
  };
  Combobox.prototype.refresh = function () {
    this.data = this.a.options();
    var v = this.a.value(), cur = null;
    for (var i = 0; i < this.data.list.length; i++) if (this.data.list[i].value === v) cur = this.data.list[i];
    this.current = cur;
    this.input.placeholder = this.isLanguage ? I18N.placeholderLanguage : I18N.placeholder;
    if (!this.open || document.activeElement !== this.input) this.input.value = cur ? cleanLabel(cur.label) : '';
    var dis = this.a.disabled ? this.a.disabled() : this.a.el.disabled;
    this.input.disabled = !!dis; this.root.classList.toggle('is-disabled', !!dis);
    this.root.classList.toggle('is-sold-out', !!(cur && cur.soldOut));
    if (this.open) this.render(this.lastQuery || '');
  };
  Combobox.prototype.onInput = function () {
    var self = this;
    if (!this.big) return this.show(this.input.value);
    clearTimeout(this._t);
    this._t = setTimeout(function () { self.show(self.input.value); }, CFG.debounceMs);
  };
  Combobox.prototype.filter = function (q) {
    var nq = norm(q), list = this.data.list, lang = this.isLanguage;
    if (!nq) return list.slice();
    var starts = [], wordStarts = [], contains = [];
    for (var i = 0; i < list.length; i++) {
      var o = list[i];
      if (o.header) continue; // headings only shown when browsing the full list
      if (o._n == null) { o._n = norm(o.label).replace(/^[*\s]+/, ''); o._a = lang ? aliasFor(o.label) : ''; }
      var idx = o._n.indexOf(nq);
      if (idx === 0) starts.push(o);
      else if (idx > 0 && /[\s(\-:/*,&]/.test(o._n.charAt(idx - 1))) wordStarts.push(o);
      else if (idx > 0 || (o._a && o._a.indexOf(nq) !== -1)) contains.push(o);
    }
    return starts.concat(wordStarts, contains);
  };
  Combobox.prototype.highlight = function (label, q) {
    var nq = norm(q); if (!nq) return esc(label);
    // Map normalized index back onto the original string (diacritics removed 1:1 per base char).
    var chars = Array.from(label), map = [], acc = '';
    chars.forEach(function (c, i) { var n = norm(c); if (/^\s$/.test(c)) n = ' '; for (var k = 0; k < n.length; k++) map.push(i); acc += n; });
    var idx = acc.indexOf(nq);
    if (idx < 0) return esc(label);
    var s = map[idx], e = map[idx + nq.length - 1] + 1;
    return esc(chars.slice(0, s).join('')) + '<mark>' + esc(chars.slice(s, e).join('')) + '</mark>' + esc(chars.slice(e).join(''));
  };
  Combobox.prototype.show = function (q) {
    if (this.input.disabled) return;
    this.open = true;
    this.root.classList.add('is-open');
    // Globo wraps selects in .gpo-select{overflow:hidden}; lift the host so the list isn't clipped
    this.host = this.host || this.root.closest('.gpo-element');
    if (this.host) this.host.classList.add('vs-host-open');
    this.list.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
    this.toggleBtn.setAttribute('aria-expanded', 'true');
    this.render(q);
  };
  Combobox.prototype.render = function (q) {
    var self = this;
    this.lastQuery = q;
    var all = this.filter(q);
    var curVal = this.current ? this.current.value : null;
    if (!all.length) {
      this.matches = [];
      this.list.innerHTML = '<li class="vs-empty" role="presentation">' + esc(tpl(I18N.noResults, 'q', q)) + '</li>';
      this.setActive(-1);
      this.status.textContent = tpl(I18N.noResults, 'q', q);
      return;
    }
    var total = 0; for (var t = 0; t < all.length; t++) if (!all[t].header) total++;
    var shown = all.length > CFG.renderLimit ? all.slice(0, CFG.renderLimit) : all;
    // Browsing a long list with a value already chosen beyond the render limit: pin it at the top so it is visible.
    if (!q && curVal != null && shown !== all && !shown.some(function (o) { return o.value === curVal; })) {
      shown = [this.current].concat(shown.slice(0, CFG.renderLimit - 1));
    }
    this.matches = shown;
    var shownCount = 0; for (var s = 0; s < shown.length; s++) if (!shown[s].header) shownCount++;
    this.list.innerHTML = shown.map(function (o, i) {
      if (o.header) return '<li role="presentation" class="vs-group" id="' + self.id + '-o' + i + '">' + esc(o.label) + '</li>';
      var tag = o.soldOut ? '<span class="vs-tag">' + esc(o.disabled ? I18N.unavailable : I18N.soldOut) + '</span>' : '';
      return '<li role="option" id="' + self.id + '-o' + i + '" class="vs-option' + (o.disabled ? ' is-disabled' : '') + (o.soldOut ? ' is-sold-out' : '') + '"' +
        ' data-value="' + esc(o.value) + '" aria-selected="' + (o.value === curVal ? 'true' : 'false') + '"' + (o.disabled ? ' aria-disabled="true"' : '') + '>' +
        '<span class="vs-option__label">' + self.highlight(cleanLabel(o.label), q) + '</span>' + tag + '</li>';
    }).join('') + (total > shownCount
      ? '<li role="presentation" class="vs-more">' + esc(I18N.more.replace('[shown]', shownCount).replace('[total]', total)) + '</li>' : '');
    var start = -1, i;
    if (q) { for (i = 0; i < shown.length; i++) if (!shown[i].disabled) { start = i; break; } }
    else if (curVal != null) { for (i = 0; i < shown.length; i++) if (shown[i].value === curVal) start = i; }
    this.setActive(start);
    this.status.textContent = total === 1 ? I18N.oneResult : tpl(I18N.results, 'n', total);
  };
  Combobox.prototype.setActive = function (i) {
    var prev = this.list.querySelector('.is-active'); if (prev) prev.classList.remove('is-active');
    this.active = i;
    if (i < 0) { this.input.removeAttribute('aria-activedescendant'); return; }
    var li = document.getElementById(this.id + '-o' + i);
    if (!li) return;
    li.classList.add('is-active');
    this.input.setAttribute('aria-activedescendant', li.id);
    var lt = this.list.scrollTop, lh = this.list.clientHeight, ot = li.offsetTop, oh = li.offsetHeight;
    if (ot < lt) this.list.scrollTop = ot; else if (ot + oh > lt + lh) this.list.scrollTop = ot + oh - lh;
  };
  Combobox.prototype.move = function (d) {
    if (!this.matches.length) return;
    var i = this.active, n = this.matches.length;
    for (var k = 0; k < n; k++) { i = (i + d + n) % n; if (!this.matches[i].disabled) break; }
    if (this.active === -1 && d < 0) i = n - 1;
    this.setActive(i);
  };
  Combobox.prototype.onKey = function (e) {
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); e.stopPropagation(); if (!this.open) this.show(''); else this.move(1); break;
      case 'ArrowUp': e.preventDefault(); e.stopPropagation(); if (!this.open) this.show(''); else this.move(-1); break;
      case 'Home': if (this.open && !this.input.value) { e.preventDefault(); this.setActive(0); } break;
      case 'End': if (this.open && !this.input.value) { e.preventDefault(); this.setActive(this.matches.length - 1); } break;
      case 'Enter':
        e.preventDefault(); e.stopPropagation(); // never let Enter submit the add-to-cart form
        if (this.open && this.active > -1 && !this.matches[this.active].disabled) this.choose(this.matches[this.active].value);
        else if (!this.open) this.show('');
        break;
      case 'Escape':
        if (this.open) { e.preventDefault(); e.stopPropagation(); this.close(true); }
        break;
      case 'Tab':
        if (this.open) this.close(true);
        break;
    }
  };
  Combobox.prototype.choose = function (value) {
    this.a.set(value);
    this.refresh();
    this.close(true);
    this.input.value = this.current ? cleanLabel(this.current.label) : '';
    this.root.dispatchEvent(new CustomEvent('variant-search:select', { bubbles: true, detail: { value: value } }));
  };
  Combobox.prototype.close = function (restore) {
    this.open = false;
    this.root.classList.remove('is-open');
    if (this.host) this.host.classList.remove('vs-host-open');
    this.list.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.toggleBtn.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    if (restore) this.input.value = this.current ? cleanLabel(this.current.label) : '';
  };
  Combobox.prototype.destroy = function () {
    if (this.unobserve) this.unobserve();
    if (this.root.parentNode) this.root.parentNode.removeChild(this.root);
  };

  /* ---------------- Discovery ---------------- */

  var widgets = [];
  function qualifies(adapter, min) {
    var data = adapter.options(), name = adapter.label().text;
    if (CFG.nameRe.test(name)) return data.list.length > 1;
    return data.list.length >= min;
  }
  function enhance(el, adapter, min) {
    if (el.__vs || optedOut(el)) return;
    if (el.multiple) return;
    if (!qualifies(adapter, el.getAttribute('data-variant-search') !== null ? 2 : (min || CFG.minOptions))) return;
    el.__vs = new Combobox(adapter);
    widgets.push(el.__vs);
  }
  function scan(scope) {
    scope = scope || document;
    if (!scope.querySelectorAll) return;
    scope.querySelectorAll('select[data-variant-input], select[data-variant-search]:not([data-variant-search="off"])').forEach(function (s) { enhance(s, new SelectAdapter(s, 'impulse')); });
    scope.querySelectorAll('select.cf__select[data-variant]').forEach(function (s) { enhance(s, new SelectAdapter(s, 'cardfinder')); });
    // Globo Product Options: native <select> fields (type "select"). Globo's own "dropdown" type already has a search box and is left alone.
    scope.querySelectorAll('.gpo-element select[name]').forEach(function (s) { enhance(s, new SelectAdapter(s, 'gpo'), CFG.gpoMinOptions); });
    scope.querySelectorAll('koala-dropdown').forEach(function (k) { if (k.querySelector('.koala-dropdown')) enhance(k, new KoalaAdapter(k)); });
  }
  function sweep() {
    widgets = widgets.filter(function (w) {
      if (w.a.el.isConnected) return true;
      w.destroy(); return false;
    });
  }

  var pending = false;
  function schedule() {
    if (pending) return; pending = true;
    (window.requestAnimationFrame || setTimeout)(function () { pending = false; sweep(); scan(document); });
  }
  function start() {
    scan(document);
    // Apps (Koala bundles, Globo options), the Card Finder and Impulse quick-shop/section reloads render later.
    new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var m = muts[i];
        if (m.target.closest && m.target.closest('[data-variant-search-widget]')) continue;
        if (m.addedNodes.length || m.removedNodes.length) { schedule(); return; }
      }
    }).observe(document.body, { childList: true, subtree: true });
    ['shopify:section:load', 'quickshop:opened', 'modalOpen'].forEach(function (ev) { document.addEventListener(ev, schedule); });
  }
  window.VariantSearch = { scan: scan, refresh: function () { widgets.forEach(function (w) { w.refresh(); }); }, widgets: function () { return widgets.slice(); } };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
